import Button from "components/common/Button";
import CustomCalendar from "components/common/CalendarComp.js/Calendar";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import TextInput from "components/common/InputField/TextInput";
import MultipleLineChart from "components/common/MultipleLineChart";
import { AnalyticsCard } from "components/views/Ldb/Dashboard/BusinessAnalytics/Card";
import { formatMoney, getDashboardChartData } from "helpers";
import useGetDashboardGWM from "hooks/Dashboard/useGetDashboardGWM";
import useGetProductList from "hooks/inventoryHooks/useGetProductList";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { truncate } from "lodash";
import millify from "millify";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import "react-responsive-pagination/themes/classic.css";
import { dashboardActions } from "redux/Ldb/actions";
import { getSalesChannelIcon } from "utils/get-sales-channel-icon";
import { productCategories } from "variables/productCategories";

type SingleForecastGraphProps = {
    activeForecastViewType?: number;
    isConfigureForecast?: boolean;
    salesChannel?: { [key: string]: any }[] | string[];
};

const SingleForecastGraph = (props: SingleForecastGraphProps) => {
    const { salesChannel, isConfigureForecast } = props;

    const [activeTab, setActiveTab] = useState<number>(0);
    const [activePeriod, setActivePeriod] = useState<number>(0);

    const [durationFilter, setDurationFilter] = useState<string>("This year");
    const [customFilter, setCustomFilter] = useState<string>("");
    const [startAndEndDate, setStartAndEndDate] = useState<string[]>([]);
    const [chartFilter, setChartFilter] = useState<string>("yearly");
    const [customValue, customOnChange] = useState("");
    const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
    const [categoryFilter, setCategoryFilter] = useState<string>("Finished Goods");
    const [productFilter, setProductFilter] = useState<string[]>([]);
    const [channelFilter, setChannelFilter] = useState<string>("Shopify");
    const [chartData, setChartData] = useState<{ [key: string]: any }>([]);
    const [refresh, setRefresh] = useState<boolean>(false);
    const clearSearchRef = useRef(null);
    const customRef = useRef(null);

    const dateValues = [
        {
            period: "This year",
            value: "yearly",
        },
        {
            period: "This month",
            value: "monthly",
        },
        {
            period: "custom",
            value: "custom",
        },
    ];

    const dispatch = useAppDispatch();
    const { addingForecastConfig, addForecastConfigSuccess } = useAppSelector(
        (state) => state.dashboard
    );

    const {
        data: gwmData,
        isFetching: gwmLoading,
        handleDateRangeChange,
        handleChannelChange,
        handleCategoryChange,
        handleProductChange,
        productIds,
        handleStartAndEndDateChange,
    } = useGetDashboardGWM(true, refresh, setRefresh);

    const {
        data: productData,
        productSearchList,
        handleDebouncedChangeTwo,
        handleSearchTwo,
        searchTwo,
        isLoadingMore,
    } = useGetProductList(null, "", false, false, "", false);

    const salesChannelOption = (salesChannel as string[])
        ?.filter(
            (channel) =>
                channel?.toLowerCase() === "shopify" ||
                channel?.toLowerCase() === "retail" ||
                channel?.toLowerCase() === "amazon"
        )
        ?.map((channel) => {
            return {
                label: channel,
                value: channel,
                icon: getSalesChannelIcon(channel) || null,
            };
        });

    const handleCategoryValue = (e) => {
        const { value } = e.target;
        setCategoryFilter(value);
        handleCategoryChange(value);
    };

    const handleDateChange = (e) => {
        const { value } = e.target;
        const [val, period] = value.split("-");

        setDurationFilter(period);
        setCustomFilter(val);

        if (val !== "custom") {
            handleDateRangeChange(val);
            setStartAndEndDate([]);
            setChartFilter(val);
            customOnChange("");
        } else {
            setShowCustomDate(true);
        }
    };

    const handleProductValue = (e) => {
        const { value } = e.target;

        const prodIdsCopy = [...productIds];
        const productFilterCopy = [...productFilter];
        const newValue = value?.split("-")[0];
        const newName = value?.split("-")[1];
        const isValuePresent = prodIdsCopy?.find((id) => id === newValue);

        if (isValuePresent) {
            handleProductChange(prodIdsCopy?.filter((id) => id !== newValue));
            setProductFilter(productFilterCopy?.filter((name) => name !== newName));
        } else {
            if (newValue === "") {
                handleProductChange([]);
                setProductFilter([]);
            } else {
                setProductFilter((prev) => [...prev, newName]);
                handleProductChange([...prodIdsCopy, newValue]);
            }
        }

        handleCategoryChange("");
        setCategoryFilter("");
    };

    const handleChangeValue = (e) => {
        const { value } = e.target;

        setChannelFilter(value);

        if (value === "All channels") {
            handleChannelChange("");
        } else {
            handleChannelChange(value);
        }
    };

    useEffect(() => {
        if (gwmData) {
            console.log(gwmData);
            let chartDataValues;
            if (customFilter === "custom" && startAndEndDate?.length === 2) {
                chartDataValues = getDashboardChartData(gwmData, "custom", startAndEndDate);
            } else {
                chartDataValues = getDashboardChartData(gwmData, chartFilter);
            }
            console.log({ chartDataValues });
            setChartData(chartDataValues);
        }
    }, [chartFilter, gwmData, startAndEndDate]);

    useEffect(() => {
        if (Boolean(addForecastConfigSuccess)) {
            toast.custom((t) => (
                <CustomToast t={t} message={"Configurations saved successfully"} type={"success"} />
            ));
            dispatch(dashboardActions.resetAddForecastConfig());
        }
    }, [dispatch, addForecastConfigSuccess]);

    useEffect(() => {
        if (durationFilter === "custom" && customValue[0] !== customValue[1]) {
            const dates = [
                moment(customValue?.[0]).format("MMM DD YYYY"),
                moment(customValue?.[1]).format("MMM DD YYYY"),
            ];

            setStartAndEndDate([dates?.[0], dates?.[1]]);
            if (customValue?.[0] !== customValue?.[1]) {
                handleStartAndEndDateChange(customValue);
            }
        }
    }, [durationFilter, customValue]);

    return (
        <div>
            {!isConfigureForecast && (
                <div className="absolute top-[80px] right-[32px]">
                    <div className=" w-full justify-end flex sm:hidden mb-4 sm:mb-0">
                        <Dropdown
                            handleChange={(name, value) => {
                                setActivePeriod(0);
                                // configHandleDateRangeChange(value?.split("-")[0]);
                                handleDateChange({ target: { name, value } });
                            }}
                            dropdown={
                                <div className="flex items-center justify-between rounded-[6px] shadow-buttonShadow2 py-[6px] px-[10px] xs:gap-2 !border !border-slate-100">
                                    <i className="ri-arrow-down-s-line  xs:text-lg text-[#142837]"></i>

                                    <div
                                        className={`text-g-75 font-rocGroteskMedium text-xs xs:text-[13px] inline-block ${
                                            durationFilter === "custom" && "capitalize"
                                        } `}
                                    >
                                        {startAndEndDate?.length !== 0
                                            ? ` ${startAndEndDate[0]} to ${startAndEndDate[1]}`
                                            : durationFilter || "This year"}
                                    </div>
                                </div>
                            }
                            dropdownItemsClasses="!px-0 !py-0"
                            dropdownClassName="!w-full !mb-10 !-mt-7"
                            dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-fit !w-auto`}
                            name="valueRange"
                            dropdownOptions={[
                                ...dateValues?.map((item, idx) => ({
                                    label: (
                                        <div
                                            key={idx}
                                            className={`${
                                                item.period === "custom" && "capitalize"
                                            } text-g-75 font-rocGroteskMedium text-[13px] flex justify-between
                                                        border-b border-b-slate-100 !w-[120px] px-4 py-[10px]`}
                                        >
                                            {item?.period}
                                        </div>
                                    ),
                                    value: item?.value + "-" + item?.period,
                                })),
                            ]}
                        />
                    </div>

                    <div className="">
                        <div className="flex sm:space-x-2.5  max-sm:justify-between max-sm: items-end border-b border-b-slate-200 sm:border-b-0">
                            {activeTab === 1 && (
                                <Dropdown
                                    handleChange={(name, value) => {
                                        // configHandleCategoryChange(value);
                                        handleCategoryValue({
                                            target: { name, value },
                                        });
                                        // nonFinishedGoodsHandleCategoryChange(value);
                                    }}
                                    dropdown={
                                        <div className="flex items-center justify-between sm:rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 !border-0 sm:!border sm:!border-slate-100">
                                            <div
                                                className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block`}
                                            >
                                                {categoryFilter
                                                    ? categoryFilter
                                                    : "All product types"}
                                            </div>
                                            <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                        </div>
                                    }
                                    // wholeContainerClass=" border-r  border-r-slate-200 sm:border-0 "
                                    dropdownItemsClasses="!px-0 !py-0"
                                    dropdownClassName="!mb-10 !-mt-7"
                                    dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 left-0 sm:right-0 rounded-[6px]  !w-[150px] !min-w-min
                                    `}
                                    name="valueRange"
                                    dropdownOptions={[
                                        // {
                                        //     label: (
                                        //         <div
                                        //             className=" text-g-75 font-rocGroteskMedium text-[13px] flex justify-between
                                        //                     border-b border-b-slate-100 !w-[150px] px-4 py-[10px]"
                                        //         >
                                        //             All product types
                                        //         </div>
                                        //     ),
                                        //     value: "",
                                        // },
                                        ...productCategories?.map((item) => ({
                                            label: (
                                                <div
                                                    className=" text-g-75 font-rocGroteskMedium text-[13px] flex justify-between
                                                        border-b border-b-slate-100 !w-[150px] px-4 py-[10px]"
                                                >
                                                    {item?.label}
                                                </div>
                                            ),
                                            value: item?.value,
                                        })),
                                    ]}
                                />
                            )}

                            <div className="sm:hidden w-[1px] h-[20px] xs:h-[25px] bg-slate-200"></div>
                            <div className="flex items-center gap-2">
                                {activeTab === 0 && (
                                    <Button
                                        btnText={"Refresh"}
                                        btnClassname={
                                            "!w-fit !rounded-[6px] !bg-[#E6F7E6] !text-[#0AAD0A] !shadow-buttonShadow2 !border !border-slate-100 !py-[9px] !text-g-55"
                                        }
                                        onClick={() => {
                                            setRefresh(true);
                                        }}
                                        icon={<i className="ri-refresh-line text-g-55"></i>}
                                    />
                                )}

                                <Dropdown
                                    handleChange={(name, value) => {
                                        // const prodIdsCopy = [...configProductIds];
                                        const newValue = value?.split("-")[0];
                                        // const isValuePresent = prodIdsCopy?.find((id) => id === newValue);

                                        // if (isValuePresent) {
                                        //     configHandleProductChange(
                                        //         prodIdsCopy?.filter((id) => id !== newValue)
                                        //     );
                                        // } else {
                                        //     if (newValue === "") {
                                        //         configHandleProductChange([]);
                                        //     } else {
                                        //         configHandleProductChange([...prodIdsCopy, newValue]);
                                        //     }
                                        // }

                                        handleProductValue({
                                            target: { name, value },
                                        });
                                    }}
                                    dropdown={
                                        <div className="flex items-center sm:rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 sm:!border sm:!border-slate-100">
                                            <div
                                                className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block`}
                                            >
                                                {productFilter?.length > 0
                                                    ? truncate(productFilter?.join(","), {
                                                          length: 15,
                                                      })
                                                    : "All products"}
                                            </div>
                                            <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                        </div>
                                    }
                                    // wholeContainerClass=" border-r  border-r-slate-200 sm:border-0 "
                                    dropdownItemsClasses="!px-0 !py-0"
                                    dropdownClassName="!w-full !mb-10 !-mt-7"
                                    dropdownContainerClasses={`sm:shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] 
                                            !min-h-[300px] !max-h-[300px] !w-auto !pt-0
                                    `}
                                    loader={isLoadingMore}
                                    // endOfList={
                                    //     pagination?.currentPage === pagination?.noOfPages
                                    // }
                                    name="valueRange"
                                    search={
                                        <div ref={clearSearchRef} className="">
                                            <TextInput
                                                value={searchTwo}
                                                name={"search"}
                                                type={"text"}
                                                required={false}
                                                onChange={handleSearchTwo}
                                                onInput={handleDebouncedChangeTwo}
                                                inputPlaceholder="Search product"
                                                containerClassname=""
                                                inputContainerClassname={"!h-[40px]"}
                                                // inputClassName="w-[50%]"
                                            />
                                        </div>
                                    }
                                    leaveOpen={true}
                                    dropdownOptions={[
                                        {
                                            label: (
                                                <div
                                                    className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                        border-b border-b-slate-100 !w-[190px] px-2 py-[10px]"
                                                >
                                                    <i
                                                        className={`${
                                                            productIds?.length === 0
                                                                ? "ri-checkbox-line text-carribean-green"
                                                                : "ri-checkbox-blank-line text-slate-200"
                                                        }    text-xl`}
                                                    ></i>
                                                    All products
                                                </div>
                                            ),
                                            value: "",
                                        },
                                        ...productSearchList?.map((item, idx) => ({
                                            label: (
                                                <div
                                                    key={idx}
                                                    className=" text-slate-600 font-rocGroteskMedium text-[13px] gap-2 flex items-center
                                                        border-b border-b-slate-100 !w-[190px] px-2 py-[10px]"
                                                >
                                                    <i
                                                        className={`${
                                                            productIds?.includes(item?._id)
                                                                ? "ri-checkbox-line text-carribean-green"
                                                                : "ri-checkbox-blank-line text-slate-200"
                                                        }   text-xl`}
                                                    ></i>

                                                    <div className="flex flex-col">
                                                        <p className="text-xs mb-1">{` SKU: ${item?.sku}`}</p>
                                                        <span className="text-[10px] leading-normal">
                                                            {truncate(item?.productName, {
                                                                length: 50,
                                                            })}
                                                        </span>

                                                        {/* <span className="text-xs">
                                                                    {truncate(item?.productName, {
                                                                        length: 38,
                                                                    })}
                                                                </span>

                                                                <p className="text-[10px]">{` SKU No: ${item?.sku}`}</p> */}
                                                    </div>
                                                </div>
                                            ),
                                            value: item?._id + "-" + item?.productName,
                                        })),
                                    ]}
                                />
                                <div className="sm:hidden w-[1px] h-[20px] xs:h-[25px] bg-slate-200"></div>

                                <Dropdown
                                    handleChange={(name, value) => {
                                        // configHandleChannelChange(value);
                                        handleChangeValue({
                                            target: { name, value },
                                        });
                                    }}
                                    dropdown={
                                        <div className="flex items-center justify-between rounded-[6px] sm:shadow-buttonShadow2 pt-[6px] sm:py-[6px] xs:px-[10px] xs:gap-2 border-0 sm:!border sm:!border-slate-100">
                                            <div
                                                className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block  ${
                                                    channelFilter && "capitalize"
                                                }`}
                                            >
                                                {truncate(channelFilter, {
                                                    length: 15,
                                                }) || "All channels"}
                                            </div>
                                            <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                                        </div>
                                    }
                                    dropdownItemsClasses=""
                                    dropdownClassName="!mb-10 !-mt-7"
                                    dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-[200px] !w-[190px] !min-w-min
                                    !py-0`}
                                    name="valueRange"
                                    dropdownOptions={[
                                        {
                                            label: "All channels",
                                            value: "",
                                        },
                                        ...salesChannelOption?.map((item, idx) => ({
                                            label: item.label,
                                            value: item.value,
                                            icon: item?.icon || null,
                                        })),
                                    ]}
                                />
                                <div className="">
                                    <Dropdown
                                        handleChange={(name, value) => {
                                            setActivePeriod(0);
                                            // configHandleDateRangeChange(value?.split("-")[0]);
                                            handleDateChange({ target: { name, value } });
                                        }}
                                        dropdown={
                                            <div className="flex items-center justify-between rounded-[6px] shadow-buttonShadow2 py-[6px] px-[10px] gap-2 !border !border-slate-100 max-sm:hidden">
                                                <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>

                                                <div
                                                    className={`text-g-75 font-rocGroteskMedium text-[13px] inline-block ${
                                                        durationFilter === "custom" && "capitalize"
                                                    } `}
                                                >
                                                    {startAndEndDate?.length !== 0
                                                        ? ` ${startAndEndDate[0]} to ${startAndEndDate[1]}`
                                                        : durationFilter || "This year"}
                                                </div>
                                            </div>
                                        }
                                        dropdownItemsClasses="!px-0 !py-0"
                                        dropdownClassName="!w-full !mb-10 !-mt-7"
                                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-fit !w-auto`}
                                        name="valueRange"
                                        dropdownOptions={[
                                            ...dateValues
                                                ?.filter((item) =>
                                                    activeTab === 0
                                                        ? true
                                                        : item?.value !== "custom"
                                                )
                                                ?.map((item, idx) => ({
                                                    label: (
                                                        <div
                                                            key={idx}
                                                            className={`${
                                                                item.period === "custom" &&
                                                                "capitalize"
                                                            } text-g-75 font-rocGroteskMedium text-[13px] flex justify-between
                                                        border-b border-b-slate-100 !w-[120px] px-4 py-[10px]`}
                                                        >
                                                            {item?.period}
                                                        </div>
                                                    ),
                                                    value: item?.value + "-" + item?.period,
                                                })),
                                        ]}
                                    />
                                </div>
                                <div className="relative">
                                    {showCustomDate && (
                                        <div
                                            ref={customRef}
                                            className="w-[630px]  z-[10000] absolute  right-0 top-16 px-[28px] pt-8 pb-8 bg-white
                                                    rounded-2xl shadow-[0px_0px_4px_rgba(0,0,0,0.04),0px_4px_32px_rgba(0,0,0,0.06)]"
                                        >
                                            <CustomCalendar
                                                showDoubleView={true}
                                                selectRange={true}
                                                value={customValue}
                                                onChange={customOnChange}
                                            />
                                            <div className="flex justify-end">
                                                <Button
                                                    btnText={"Close"}
                                                    btnClassname={"!w-fit !py-2"}
                                                    onClick={() => setShowCustomDate(false)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div>
                <div className="lg:flex justify-between items-start mt-8">
                    <div className="flex flex-col gap-3.5 mb-6 lg:mb-0">
                        {gwmLoading ? (
                            <Skeleton
                                height={10}
                                width={150}
                                highlightColor="#E2E8F0"
                                borderRadius={16}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        ) : (
                            <div className="flex items-center">
                                <img
                                    src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1723568484/blue_line_yds7b9.svg"
                                    alt="this-trend"
                                    className="w-4 h-4"
                                />
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    Current sales
                                </p>
                            </div>
                        )}

                        {gwmLoading ? (
                            <Skeleton
                                height={10}
                                width={150}
                                highlightColor="#E2E8F0"
                                borderRadius={16}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        ) : (
                            <div className="flex items-center">
                                <img
                                    src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1712800595/chartLine2_o41vii.svg"
                                    alt="this-trend"
                                    className="w-4 h-4"
                                />
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    My configured plan
                                </p>
                            </div>
                        )}

                        {gwmLoading ? (
                            <Skeleton
                                height={10}
                                width={150}
                                highlightColor="#E2E8F0"
                                borderRadius={16}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        ) : (
                            <div className="flex items-center">
                                <img
                                    src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1723568484/red_line_yfjgnt.svg"
                                    alt="this-trend"
                                    className="w-4 h-4"
                                />
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    Synth's predicted forecast
                                </p>
                            </div>
                        )}

                        {gwmLoading ? (
                            <Skeleton
                                height={10}
                                width={150}
                                highlightColor="#E2E8F0"
                                borderRadius={16}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        ) : (
                            <div className="flex items-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 16 15"
                                >
                                    <path
                                        stroke="#6562E5"
                                        strokeDasharray="2 2"
                                        strokeWidth="1.5"
                                        d="M0 8.5h3.5l2.5-6 3.5 10 1.5-4h5"
                                    ></path>
                                </svg>
                                <p className="text-slate-700 font-rocGroteskMedium text-[10px] leading-3 ml-1">
                                    My saved target{" "}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="flex max-lg:flex-col">
                        <div className="flex w-full lg:w-fit max-lg:items-center max-lg:justify-between max-lg:border-b max-lg:border-b-slate-200">
                            {gwmLoading ? (
                                <div className="flex flex-col gap-2 items-end pr-10">
                                    <Skeleton
                                        height={10}
                                        width={90}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                    <Skeleton
                                        height={10}
                                        width={40}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                    <Skeleton
                                        height={10}
                                        width={200}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                </div>
                            ) : (
                                <AnalyticsCard
                                    title={"Forecasted revenue"}
                                    figure={
                                        gwmData?.forecastTotalSale !== undefined
                                            ? millify(Number(gwmData?.forecastTotalSale), {
                                                  precision: 2,
                                              })
                                            : "----"
                                    }
                                    currency={Boolean(gwmData?.forecastTotalSale) && "$"}
                                    customFigure={
                                        Boolean(gwmData?.configuredRevForecast) &&
                                        gwmData?.configuredRevForecast !== undefined
                                            ? millify(Number(gwmData?.configuredRevForecast), {
                                                  precision: 2,
                                              })
                                            : "----"
                                    }
                                    loader={false}
                                    iconTwo
                                    hideDirection={true}
                                    hasCustomValue={true}
                                    increaseClassName="!text-g-60"
                                    innerClassName="!justify-end"
                                    addTextTwoClass="!whitespace-nowrap"
                                    addTextTwo={"Synth's revenue forecast"}
                                    customSubTextClass="!whitespace-nowrap"
                                    customSubText={"Configured revenue forecast"}
                                    className="!shadow-none !border-y-0 !border-l-0 lg:!border-r lg:!border-r-slate-200 !py-2 !pl-0 lg:!pl-[15px] !pr-8  !rounded-none flex-1 lg:!w-auto"
                                    arrUpClass={"!text-g-60"}
                                    titleClassName="!text-slate-600 !text-[10px]"
                                    figureClassName="!mt-2.5 !text-slate-600  !text-[20px] !leading-[28px]"
                                    tooltipClassName="!max-w-[195px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-l-transparent before:!border-r-gm-75 before:!top-[40%] before:!-left-[9%] !z-[999] before:!border-[6px] !left-[50px] xs:!left-[120px]"
                                    toolTipContent={
                                        <div className="!p-3">
                                            <p className="underline text-xs font-rocGroteskMedium mb-1">
                                                Forecasted revenue
                                            </p>
                                            <p className="text-[11px]">
                                                This is the estimated amount of money that you are
                                                likely to earn over a future period. This is
                                                calculated using external factors such as
                                                seasonality, social media, supplier lead time, and
                                                historical sales.
                                            </p>
                                        </div>
                                    }
                                    addTextTwoTooltipContent={
                                        "This is the total amount our system has forecasted you would sell for all your inventory over the next 6 months. We use the current unit inventory value/amount multiplied by the forecasted quantity."
                                    }
                                    customSubTextTooltipContent={
                                        "This is the total forecasted revenue for all your inventory that you have configured over the next 6 months. We use the current unit inventory value/amount multiplied by the forecasted quantity you set."
                                    }
                                />
                            )}

                            <div className="h-[29px] lg:hidden w-[1px] bg-slate-200"></div>

                            {gwmLoading ? (
                                <div className="flex flex-col gap-2 items-end pr-10">
                                    <Skeleton
                                        height={10}
                                        width={90}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                    <Skeleton
                                        height={10}
                                        width={40}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                    <Skeleton
                                        height={10}
                                        width={200}
                                        highlightColor="#E2E8F0"
                                        borderRadius={16}
                                        baseColor="#F1F5F9"
                                        className="custom-skeleton"
                                    />
                                </div>
                            ) : (
                                <AnalyticsCard
                                    title={"Forecasted sales quantity"}
                                    value={Math.round(gwmData?.totalForecastQty)}
                                    customFigure={
                                        gwmData?.configuredDemandForecast
                                            ? formatMoney().format(
                                                  gwmData?.configuredDemandForecast
                                              )
                                            : "----"
                                    }
                                    loader={false}
                                    iconTwo
                                    hasCustomValue={true}
                                    className="!shadow-none !border-y-0 !border-l-0 lg:!border-r lg:!border-r-slate-200 !py-2 !pl-[15px] !pr-8  !rounded-none  flex-1 lg:!w-fit"
                                    titleClassName="!text-slate-500 !text-[10px]"
                                    innerClassName="!justify-end"
                                    addTextTwoClass="!whitespace-nowrap"
                                    addTextTwo={"Synth's demand forecast"}
                                    customSubTextClass="!whitespace-nowrap"
                                    customSubText={"Configured demand forecast"}
                                    figureClassName="!mt-2.5 !text-slate-600  !text-[20px] !leading-[28px]"
                                    tooltipClassName="!max-w-[195px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-r-transparent lg:before:!border-l-transparent before:!border-l-gm-75 lg:before:!border-r-gm-75 before:!top-[40%] before:!-right-[95px] lg:before:!right-auto lg:before:!-left-[9%] !z-[999] before:!border-[6px] !-left-[100px] lg:!left-[120px]"
                                    toolTipContent={
                                        <div className="!p-3">
                                            <p className="underline text-xs font-rocGroteskMedium mb-1">
                                                Forecasted sales quantity
                                            </p>
                                            <p className="text-[11px]">
                                                This is the estimated number of units of all
                                                products that you are likely to sell in a future
                                                period.
                                            </p>
                                        </div>
                                    }
                                    addTextTwoTooltipContent={
                                        "This is the total number of units across all your inventory that you are most likely going to sell over the next 6 months."
                                    }
                                    customSubTextTooltipContent={
                                        "This is the total quantity across all your inventory that you have setup as your likely forecasted units to be sold."
                                    }
                                />
                            )}
                        </div>

                        {gwmLoading ? (
                            <div className="flex flex-col gap-2 items-end pr-10">
                                <Skeleton
                                    height={10}
                                    width={90}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                                <Skeleton
                                    height={10}
                                    width={40}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                                <Skeleton
                                    height={10}
                                    width={200}
                                    highlightColor="#E2E8F0"
                                    borderRadius={16}
                                    baseColor="#F1F5F9"
                                    className="custom-skeleton"
                                />
                            </div>
                        ) : (
                            <AnalyticsCard
                                title={"Forecasted inventory deficit"}
                                value={Math.round(gwmData?.forecastedStockNeeded)}
                                loader={false}
                                iconTwo
                                addTextTwo={"(oos)"}
                                addTextTwoClass="!text-r-55 !text-xl"
                                textAndSubtextContainer={"!justify-end"}
                                className="!shadow-none !border-none !py-2 !pl-0 lg:!pl-[15px] !pr-8  !rounded-none !w-auto"
                                titleClassName="!text-slate-600 !text-[10px]"
                                figureClassName="!mt-2.5 !text-r-55  !text-[20px] !leading-[28px] flex gap-1 items-end"
                                tooltipClassName="!max-w-[195px] !bg-gm-75  !rounded-[12px] before:!border-y-transparent before:!border-l-transparent lg:before:!border-r-transparent before:!border-r-gm-75 lg:before:!border-l-gm-75 before:!top-[40%] before:!-left-[9%] lg:before:!left-auto lg:before:!-right-[6px] !z-[999] before:!border-[6px] !left-[0px] xs:!left-[120px] lg:!-left-[250px]"
                                toolTipContent={
                                    <div className="!p-3">
                                        <p className="underline text-xs font-rocGroteskMedium mb-1">
                                            Forecasted inventory deficit
                                        </p>
                                        <p className="text-[11px] ">
                                            This is the estimated number of units of all your
                                            products that are likely to be out of stock in a future
                                            period. These are the likely out of stock units of
                                            products with respect to the demand forecast.
                                        </p>
                                    </div>
                                }
                                addTextTwoTooltipContent={
                                    "This is the total number of units you would need to meet up the forecasted sales quantity over the next 6 months, based on your current inventory level today."
                                }
                            />
                        )}
                    </div>
                </div>

                {productData?.length !== 0 &&
                !gwmLoading &&
                gwmData?.currentSales?.length === 0 &&
                gwmData?.forcastSales?.length === 0 ? (
                    <div className="w-[100%] h-[400px] flex items-center justify-center">
                        <div className="flex items-center  gap-[3px] flex-col w-[353px] justify-center">
                            <img
                                src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1717426949/icon_w8c4mn.png"
                                alt=""
                                className=""
                            />
                            <p className="text-g-75 font-rocGroteskBold text-base ">
                                {" "}
                                No forecast data to show for this query{" "}
                            </p>
                        </div>
                    </div>
                ) : gwmLoading ? (
                    <div className="mt-20">
                        <div className="flex gap-10 mb-5">
                            <div className="flex flex-col gap-[17px]">
                                <span className="block text-[9px] text-slate-500">70k</span>
                                <span className="block text-[9px] text-slate-500">60k</span>
                                <span className="block text-[9px] text-slate-500">50k</span>
                                <span className="block text-[9px] text-slate-500">40k</span>
                                <span className="block text-[9px] text-slate-500">30k</span>
                                <span className="block text-[9px] text-slate-500">20k</span>
                                <span className="block text-[9px] text-slate-500">10k</span>
                            </div>

                            <div className="">
                                <svg
                                    className="w-full h-48 skeleton-pulse"
                                    viewBox="0 0 1000 200"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        d="M0,150 C60,100 120,60 180,30 C240,10 280,20 320,40 C380,70 440,90 500,100 C560,110 600,100 640,80 C680,60 720,40 760,25 C800,10 850,5 900,0 C950,0 975,0 1000,0"
                                        stroke="#E2E8F0"
                                        stroke-width="6"
                                        fill="none"
                                    />
                                </svg>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 justify-between pt-[15px] border-t border-slate-200 ml-10">
                            <span className="block text-[9px] text-slate-500">Jan</span>
                            <span className="block text-[9px] text-slate-500">Feb</span>
                            <span className="block text-[9px] text-slate-500">Mar</span>
                            <span className="block text-[9px] text-slate-500">Apr</span>
                            <span className="block text-[9px] text-slate-500">May</span>
                            <span className="block text-[9px] text-slate-500">Jun</span>
                        </div>
                    </div>
                ) : (
                    <div className="mt-20 mb-6">
                        <MultipleLineChart mapData={chartData} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SingleForecastGraph;
